import Educator from 'aquilla/core/organisms/EducatorCard';
import styled from '@emotion/styled';
import EducatorThumbnailBackground from 'aquilla/core/atoms/EducatorThumbnailBackground';
import Image from '@base/Image';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import { Spacings, mediaQuery, Fonts } from '@styles/index';

import { Typography } from '@material-ui/core';
import { getImage } from '@utils/imageHelper';
import useWindowSize from '@hooks/useWindowSize';
import { fetchEducatorsDetails } from '@api/fetchers/citySpecificOfflineCentre';
import { useEffect, useState } from 'react';
import letterColorMapping from './colorMapping';
import GridContainer from './CommonComponents';

const EduThumbWrapper = styled.div`
  position: relative;
`;

const Container = styled.div`
  height: auto;
  background-color: var(--color-i-yellow-11);
  width: 100%;
  background-image: url(${({ bg }) => getImage(bg)});
`;

const StyGridContainer = styled(GridContainer)`
  margin-top: ${Spacings.SPACING_16B};
  margin-bottom: ${Spacings.SPACING_16B};

  ${mediaQuery.uptoMobile} {
    padding-right: 0px !important;
    padding-left: 0px !important;
    margin-top: ${Spacings.SPACING_8B};
    margin-bottom: ${Spacings.SPACING_8B};
  }
`;

const EducatorCarouselWrapper = styled.div`
  grid-column: span 24;
  width: 100%;
`;

const EduImage = styled(Image)`
  object-fit: contain;
`;

const EduImageWrapper = styled.div`
  position: absolute;
  bottom: 0px;
  left: 50%;
  transform: translateX(-50%);
  width: ${({ width }) => width};
  height: ${({ height }) => height};
`;

const StyEducator = styled(Educator)`
  align-items: stretch;
  width: 560px;
  max-width: 560px !important;
  cursor: pointer;
  & .aquilla-typography {
    -webkit-line-clamp: 5;
  }
  ${mediaQuery.uptoMobile} {
    width: 200px;
    & .aquilla-typography {
      -webkit-line-clamp: 2;
    }
  }
`;

const StyledCombineCards = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${Spacings.SPACING_6B};
  column-gap: ${Spacings.SPACING_6B};
`;

const StyledHeading = styled(Typography)`
  font-size: ${Fonts.H2};
  font-weight: ${Fonts.HEAVY};
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H4};
    font-weight: ${Fonts.HEAVY};
  }
`;

const StyledEducSubHeading = styled(Typography)`
  color: var(--color-text-secondary);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H5};
    font-weight: ${Fonts.LIGHT};
    line-height: ${Spacings.SPACING_6B};
  }
`;

const ColoredHeading = styled.span`
  color: var(--color-i-violet-15);
`;

const StyShowCase = styled(ShowCase)`
  & > .aquilla-showcase-section-header {
    align-items: flex-start;
    margin-bottom: ${Spacings.SPACING_8B};
    ${mediaQuery.uptoMobile} {
      margin-bottom: ${Spacings.SPACING_6B};
    }
    & > .aquilla-showcase-section-header-heading {
      width: 100%;
    }
  }
  & > .aquilla-carousel-wrapper:last-child {
    margin-right: 16px;
  }
`;

const ImageComp = ({ url, width, height, color }) => {
  return (
    <EduThumbWrapper>
      <EducatorThumbnailBackground color={color} />
      <EduImageWrapper width={width} height={height}>
        <EduImage src={url} alt="edu-image" layout="fill" />
      </EduImageWrapper>
    </EduThumbWrapper>
  );
};

const EducatorBlock = ({ data }) => {
  const { educators } = data;
  const { isMobile } = useWindowSize();
  const [educatorDetails, setEducatorDetails] = useState([]);

  const getEducatorDetails = () => {
    const educatorUsernames = educators.map((edu) => {
      return edu.educatorUsername;
    });
    if (educatorUsernames?.length > 0) {
      const body = {
        educator_usernames: educatorUsernames
      };

      fetchEducatorsDetails({ body }).then((res) => {
        if (res?.educatorsDetail?.length > 0) {
          setEducatorDetails(res?.educatorsDetail);
        }
      });
    }
  };

  useEffect(() => {
    getEducatorDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderCard = (card) => {
    if (!card?.name) return null;

    const currentEducatorDetails =
      educatorDetails?.find(
        (educator) =>
          educator.username.toLowerCase() ===
          card.educatorUsername.toLowerCase()
      ) || {};

    const {
      firstName,
      lastName,
      bio,
      oldAvatar,
      avatar_v1,
      permalink,
      followersCount,
      followersLabel
    } = currentEducatorDetails;
    const name = `${firstName} ${lastName}`;

    const { properties, subject } = card || {};
    const subjectInfo = {
      name: '',
      color: ''
    };

    let newProperties = [];
    newProperties = properties.concat({
      value: followersCount,
      title: followersLabel
    });
    newProperties = newProperties.filter(
      (property) => property.title !== 'Students'
    );

    const subjectColor = letterColorMapping[subject?.[0]?.toLowerCase()] || '';
    const NewImageComp = () => (
      <ImageComp
        url={oldAvatar || avatar_v1}
        width="128px"
        height="160px"
        color={subjectColor}
      />
    );

    const educatorLink = `${permalink}/?source=CGP`;

    return (
      <StyEducator
        subjectInfo={subjectInfo}
        key={card?.thumbnail}
        thumbnail={NewImageComp}
        hideCTA
        properties={newProperties}
        educatorBio={bio}
        educatorName={name}
        variant="descriptive"
        as="a"
        href={educatorLink}
      />
    );
  };

  const combineCards = (cards) => {
    const indents = [];
    for (let i = 0; i < cards?.length; i += 4) {
      indents.push(
        <StyledCombineCards>
          {renderCard(cards?.[i])}
          {cards?.[i + 2] ? renderCard(cards[i + 2]) : null}
        </StyledCombineCards>
      );
      indents.push(
        <StyledCombineCards>
          {renderCard(cards?.[i + 1])}
          {cards?.[i + 3] ? renderCard(cards[i + 3]) : null}
        </StyledCombineCards>
      );
    }
    return indents.map((indent) => {
      return <>{indent}</>;
    });
  };

  const singleCards = (cards) => {
    return cards.map((card) => {
      return renderCard(card);
    });
  };

  const renderHeading = () => {
    return (
      <>
        <StyledHeading variant="h4">
          India’s <ColoredHeading>top educators</ColoredHeading>
        </StyledHeading>
        <StyledEducSubHeading variant="p1">
          We bring you the best educators from the country
        </StyledEducSubHeading>
      </>
    );
  };

  const renderEducators = () => {
    return (
      <StyShowCase title={renderHeading()} itemsPerSlide={2}>
        {isMobile ? singleCards(educators) : combineCards(educators)}
      </StyShowCase>
    );
  };

  if (!educatorDetails?.length > 0) return null;

  return (
    <Container
      bg={
        isMobile
          ? 'offline-centres/educator-block-mweb-bg.svg'
          : 'offline-centres/educator_block_bg_graphics.svg'
      }
    >
      <StyGridContainer>
        <EducatorCarouselWrapper>{renderEducators()}</EducatorCarouselWrapper>
      </StyGridContainer>
    </Container>
  );
};

export default EducatorBlock;
